// src/pages/Dashboard/Admin/Coaching/components/CoachingHoursChartPanel.jsx

import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { FaChartColumn } from "react-icons/fa6";
import { formatHours } from "../utils/coachingDashboardUtils";

function useDarkModeFlag() {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;

    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

function CustomTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;

  const hours = payload[0]?.value || 0;
  const events = payload[0]?.payload?.events || 0;

  return (
    <div
      className={`rounded-2xl border px-3 py-2 shadow-sm ${
        isDark
          ? "border-slate-700 bg-slate-900 text-slate-100"
          : "border-slate-200 bg-white text-slate-800"
      }`}
    >
      <div className="text-[11px] opacity-70">{label}</div>
      <div className="mt-1 text-sm font-bold">{formatHours(hours)}</div>
      <div className="text-xs opacity-70">{events} événement(s)</div>
    </div>
  );
}

export default function CoachingHoursChartPanel({ data, loading }) {
  const isDark = useDarkModeFlag();

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark
    ? "rgba(148,163,184,0.12)"
    : "rgba(148,163,184,0.15)";

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Synthèse globale
          </p>

          <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <FaChartColumn className="text-indigo-600 dark:text-indigo-300" />
            Heures de coaching par enseignant
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Visualisation des heures cumulées pour comparer rapidement la charge
            de coaching par enseignant.
          </p>
        </div>

        {loading ? (
          <div className="h-[340px] w-full animate-pulse rounded-[22px] bg-slate-100 dark:bg-slate-800" />
        ) : data.length === 0 ? (
          <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            Aucune donnée de coaching disponible.
          </div>
        ) : (
          <div className="h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 22, right: 16, left: 4, bottom: 54 }}
              >
                <defs>
                  <linearGradient id="coachingBarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4CA82F" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#013E8E" stopOpacity={0.85} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" stroke={grid} />

                <XAxis
                  dataKey="nom"
                  interval={0}
                  angle={-25}
                  dy={24}
                  height={62}
                  tick={{ fill: tick, fontSize: 12 }}
                />

                <YAxis
                  tick={{ fill: tick, fontSize: 12 }}
                  width={42}
                  tickFormatter={(value) =>
                    Number(value).toLocaleString("fr-FR", {
                      maximumFractionDigits: 1,
                    })
                  }
                />

                <Tooltip
                  cursor={{ fill: cursorFill }}
                  content={<CustomTooltip isDark={isDark} />}
                />

                <Bar
                  dataKey="hours"
                  fill="url(#coachingBarFill)"
                  radius={[10, 10, 0, 0]}
                >
                  <LabelList
                    dataKey="hours"
                    position="top"
                    formatter={(value) =>
                      Number(value).toLocaleString("fr-FR", {
                        maximumFractionDigits: 2,
                      })
                    }
                    style={{
                      fill: isDark ? "#e2e8f0" : "#0f172a",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}